import { Path, Waypoint } from '../../Components/PathBuilder/pathBuilderTypes';
import tinygradient from 'tinygradient';
import { PATH_BUILDER_SPEED_CONSTRAINTS, waypointConstraints } from '../constants';
import { clampCoordinateMeters, roundCoordinateMeters } from '../fieldSelection';

export function buildWaypointId(pathName: string, index: number): string {
  return `${pathName}_${index + 1}`;
}

export interface FieldPoint2D {
  x: number;
  y: number;
}

/** Centimeters per meter — used by project migrations only. */
export const CM_PER_M = 100;

/** Field geometry from `getFieldGeometry()` is authored in cm; Path Builder uses meters. */
export const fieldSizeMeters = (fieldSizeCm: number) => roundCoordinateMeters(fieldSizeCm / CM_PER_M);

/**
 * Field space (stored): origin lower-left, +X right, +Y up, units in meters.
 * SVG space: origin upper-left, +X right, +Y down, units in meters.
 */
export const fieldYToSvgY = (fieldY: number, fieldHeight: number) => fieldHeight - fieldY;

export const svgYToFieldY = (svgY: number, fieldHeight: number) => fieldHeight - svgY;

export const fieldPointToSvg = <T extends FieldPoint2D>(point: T, fieldHeight: number): T => ({
  ...point,
  y: fieldYToSvgY(point.y, fieldHeight),
});

export const svgPointToField = <T extends FieldPoint2D>(point: T, fieldHeight: number): T => ({
  ...point,
  x: roundCoordinateMeters(point.x),
  y: roundCoordinateMeters(svgYToFieldY(point.y, fieldHeight)),
});

export const waypointsToSvg = (waypoints: Waypoint[], fieldHeight: number): Waypoint[] => waypoints.map((wp) => fieldPointToSvg(wp, fieldHeight));

/**
 * SVG `rotate()` angle (clockwise positive) so field yaw 0 (+X) faces right when the
 * robot artwork faces local −Y (up on screen) at rotate(0).
 */
export const fieldYawToSvgRotate = (fieldYawDegrees: number) => 90 - fieldYawDegrees;

/** Supported path-builder field view rotations (degrees clockwise from natural orientation). */
export type FieldViewRotation = 0 | 90 | 180 | 270;

export const normalizeFieldViewRotation = (rotation: number): FieldViewRotation => {
  const normalized = (((Math.round(rotation) % 360) + 360) % 360) as number;
  if (normalized === 90 || normalized === 180 || normalized === 270) return normalized;
  return 0;
};

/**
 * SVG transform that maps the natural field rectangle (0,0)–(width,height) into the
 * viewBox that may swap width/height for 90°/270°. Paths and axes should live under this transform.
 */
export const getFieldViewTransform = (rotation: number, naturalWidth: number, naturalHeight: number): string | undefined => {
  switch (normalizeFieldViewRotation(rotation)) {
    case 90:
      return `translate(${naturalHeight} 0) rotate(90)`;
    case 180:
      return `translate(${naturalWidth} ${naturalHeight}) rotate(180)`;
    case 270:
      return `translate(0 ${naturalWidth}) rotate(-90)`;
    default:
      return undefined;
  }
};

/** Map a point from natural SVG space (unrotated field) into viewBox space. */
export const naturalSvgPointToView = (point: FieldPoint2D, rotation: number, naturalWidth: number, naturalHeight: number): FieldPoint2D => {
  const { x, y } = point;
  switch (normalizeFieldViewRotation(rotation)) {
    case 90:
      return { x: naturalHeight - y, y: x };
    case 180:
      return { x: naturalWidth - x, y: naturalHeight - y };
    case 270:
      return { x: y, y: naturalWidth - x };
    default:
      return { x, y };
  }
};

/** Inverse of {@link naturalSvgPointToView}: viewBox space → natural SVG space. */
export const viewPointToNaturalSvg = (point: FieldPoint2D, rotation: number, naturalWidth: number, naturalHeight: number): FieldPoint2D => {
  const { x, y } = point;
  switch (normalizeFieldViewRotation(rotation)) {
    case 90:
      return { x: y, y: naturalHeight - x };
    case 180:
      return { x: naturalWidth - x, y: naturalHeight - y };
    case 270:
      return { x: naturalWidth - y, y: x };
    default:
      return { x, y };
  }
};

/** ViewBox pointer → stored field coordinates (view rotation + Y-up convention). */
export const viewPointToField = (point: FieldPoint2D, rotation: number, naturalWidth: number, naturalHeight: number): FieldPoint2D =>
  svgPointToField(viewPointToNaturalSvg(point, rotation, naturalWidth, naturalHeight), naturalHeight);

/** Stored field coordinates → viewBox space (for overflow / hit metrics). */
export const fieldPointToView = (point: FieldPoint2D, rotation: number, naturalWidth: number, naturalHeight: number): FieldPoint2D =>
  naturalSvgPointToView(fieldPointToSvg(point, naturalHeight), rotation, naturalWidth, naturalHeight);

/** Extra scroll padding so axis arrows just outside the field stay visible. */
export const PATH_BUILDER_AXIS_OVERHANG_M = 0.28;

/**
 * Tiny start offset for SVG arc path commands so consecutive A segments connect cleanly.
 * Legacy cm-space value was 0.01 cm → 0.0001 m after the meters conversion.
 */
const ARC_PATH_START_EPSILON_M = 0.0001;

/**
 * Read waypoints from a path that may still use the legacy `waypoints` key (pre-migration).
 * Prefer a non-empty list so an empty `wp: []` cannot shadow real legacy `waypoints`.
 * When both lists are non-empty, prefer compact `wp`.
 *
 * Generic so callers can pass legacy disk shapes (`id?`, `radius`/`rotation`, etc.) as well as {@link Waypoint}.
 */
export function getPathWaypoints<T = Waypoint>(path: { wp?: T[]; waypoints?: T[] }): T[] {
  const wp = path.wp;
  const waypoints = path.waypoints;
  if (Array.isArray(wp) && wp.length > 0) return wp;
  if (Array.isArray(waypoints) && waypoints.length > 0) return waypoints;
  return wp ?? waypoints ?? [];
}

/** Sets path.id = path.name and assigns index-based waypoint ids. */
export function assignPathAndWaypointIds(path: Path): Path {
  const id = path.name;
  const wp = getPathWaypoints(path).map((waypoint, i) => ({ ...waypoint, id: buildWaypointId(path.name, i) }));
  return { ...path, id, wp };
}

/** Re-assigns only waypoint ids based on current index (after insert or delete). */
export function reassignWaypointIds(path: Path): Path {
  const wp = getPathWaypoints(path).map((waypoint, i) => ({ ...waypoint, id: buildWaypointId(path.name, i) }));
  return { ...path, wp };
}

/**
 * True while the user is mid-typing a signed number (empty or a lone minus).
 * Used so controlled heading inputs can keep "-" without coercing to 0.
 */
export function isTransientSignedNumberDraft(value: string): boolean {
  const trimmed = value.trim();
  return trimmed === '' || trimmed === '-';
}

export const checkConstraint = <K extends keyof Waypoint>(
  field: K,
  value: string,
  coordinateBounds?: {
    xMax?: number;
    yMax?: number;
  }
) => {
  const numericValue = Number(value);

  if (field === 'x') {
    return clampCoordinateMeters(numericValue, coordinateBounds?.xMax ?? numericValue) as Waypoint[K];
  }

  if (field === 'y') {
    return clampCoordinateMeters(numericValue, coordinateBounds?.yMax ?? numericValue) as Waypoint[K];
  }

  // Heading: integer degrees in canonical [−180, 180) (round first, then wrap).
  if (field === 'rot') {
    return canonicalizeRotationDegrees(numericValue) as Waypoint[K];
  }

  const constraint = waypointConstraints[field as keyof typeof waypointConstraints];
  if (!constraint) {
    return value;
  }

  const { minValue, maxValue } = constraint;

  return roundCoordinateMeters(Math.max(minValue, Math.min(maxValue, numericValue))) as Waypoint[K];
};

/**
 * Rotate a waypoint 90° in the path-builder field plane (x right, y up, origin bottom-left).
 * After rotation the caller should treat field size as swapped (width ↔ height).
 */
/** Rotate a waypoint 90° in stored field meters (bounds also in meters). */
export const rotateWaypoint90InField = (wp: Waypoint, fieldWidthM: number, fieldHeightM: number, direction: 'cw' | 'ccw'): Waypoint => {
  const { x, y, rot, ...rest } = wp;
  if (direction === 'cw') {
    return {
      ...rest,
      x: roundCoordinateMeters(y),
      y: roundCoordinateMeters(fieldWidthM - x),
      rot: canonicalizeRotationDegrees(rot + 90),
    };
  }
  return {
    ...rest,
    x: roundCoordinateMeters(fieldHeightM - y),
    y: roundCoordinateMeters(x),
    rot: canonicalizeRotationDegrees(rot - 90),
  };
};

const distance = (a: Waypoint, b: Waypoint) => {
  return Math.hypot(a.x - b.x, a.y - b.y);
};

const angleAt = (prev: Waypoint, curr: Waypoint, next: Waypoint): number => {
  const v1x = prev.x - curr.x;
  const v1y = prev.y - curr.y;
  const v2x = next.x - curr.x;
  const v2y = next.y - curr.y;
  const dot = v1x * v2x + v1y * v2y;
  const mag1 = Math.hypot(v1x, v1y);
  const mag2 = Math.hypot(v2x, v2y);
  return Math.acos(Math.max(-1, Math.min(1, dot / (mag1 * mag2))));
};

/**
 * Given two adjacent corners (Q and R), shrink radii so they don't overlap.
 * Returns new radii for [Q, R].
 */
const fixPair = (P: Waypoint, Q: Waypoint, R: Waypoint, S: Waypoint): [number, number] => {
  const len = distance(Q, R);

  let rQ = Q.rad;
  let rR = R.rad;

  if (rQ <= 0 && rR <= 0) return [0, 0];

  if (rQ > 0 && rR <= 0) {
    const θQ = angleAt(P, Q, R);
    const tQ = Math.tan(θQ / 2);
    if (tQ > 0) {
      const sQ = rQ / tQ;
      if (sQ > len) {
        rQ = len * tQ;
      }
    }
    return [rQ, 0];
  }

  if (rR > 0 && rQ <= 0) {
    const θR = angleAt(Q, R, S);
    const tR = Math.tan(θR / 2);
    if (tR > 0) {
      const sR = rR / tR;
      if (sR > len) {
        rR = len * tR;
      }
    }
    return [0, rR];
  }

  const θQ = angleAt(P, Q, R);
  const θR = angleAt(Q, R, S);

  const tQ = Math.tan(θQ / 2);
  const tR = Math.tan(θR / 2);

  if (tQ <= 0 || tR <= 0) return [rQ, rR];

  const sQ = rQ / tQ;
  const sR = rR / tR;

  const overlap = sQ + sR - len;

  if (overlap > 0) {
    const shrinkQ = (sQ / (sQ + sR)) * overlap;
    const shrinkR = (sR / (sQ + sR)) * overlap;

    rQ = rQ - shrinkQ * tQ;
    rR = rR - shrinkR * tR;
  }

  return [rQ, rR];
};

export const checkWaypointRadii = (waypoints: Waypoint[], i: number): Waypoint[] => {
  const newWaypoints = [...waypoints];

  let changed = true;
  while (changed) {
    changed = false;

    for (let j = Math.max(1, i); j > 0; j--) {
      const [rPrev, rCurr] = fixPair(newWaypoints[j - 2], newWaypoints[j - 1], newWaypoints[j], newWaypoints[j + 1]);
      if (rPrev !== newWaypoints[j - 1].rad || rCurr !== newWaypoints[j].rad) {
        changed = true;
        newWaypoints[j - 1] = { ...newWaypoints[j - 1], rad: rPrev };
        newWaypoints[j] = { ...newWaypoints[j], rad: rCurr };
      }
    }

    for (let j = i; j < newWaypoints.length - 1; j++) {
      const [rCurr, rNext] = fixPair(newWaypoints[j - 1], newWaypoints[j], newWaypoints[j + 1], newWaypoints[j + 2]);
      if (rCurr !== newWaypoints[j].rad || rNext !== newWaypoints[j + 1].rad) {
        changed = true;
        newWaypoints[j] = { ...newWaypoints[j], rad: rCurr };
        newWaypoints[j + 1] = { ...newWaypoints[j + 1], rad: rNext };
      }
    }
  }

  return newWaypoints;
};

export const computeTangentPoints = (p1: Waypoint, p2: Waypoint, p3: Waypoint, radius: number) => {
  const v1 = { x: p1.x - p2.x, y: p1.y - p2.y };
  const v2 = { x: p3.x - p2.x, y: p3.y - p2.y };

  const normalize = (v: { x: number; y: number }) => {
    const len = Math.hypot(v.x, v.y);
    return { x: v.x / len, y: v.y / len };
  };

  const nv1 = normalize(v1);
  const nv2 = normalize(v2);

  const angle = Math.acos(nv1.x * nv2.x + nv1.y * nv2.y);
  const tanDist = radius / Math.tan(angle / 2);

  const t1 = {
    x: p2.x + nv1.x * tanDist,
    y: p2.y + nv1.y * tanDist,
  };
  const t2 = {
    x: p2.x + nv2.x * tanDist,
    y: p2.y + nv2.y * tanDist,
  };

  const bisector = normalize({ x: nv1.x + nv2.x, y: nv1.y + nv2.y });
  const lengthToCenter = radius / Math.sin(angle / 2);
  const center = {
    x: p2.x + bisector.x * lengthToCenter,
    y: p2.y + bisector.y * lengthToCenter,
  };

  const toT1 = { x: t1.x - center.x, y: t1.y - center.y };
  const toT2 = { x: t2.x - center.x, y: t2.y - center.y };

  const bisectorDir = normalize({ x: toT1.x + toT2.x, y: toT1.y + toT2.y });

  const b = {
    x: center.x + bisectorDir.x * radius,
    y: center.y + bisectorDir.y * radius,
  };

  return { b, t1, t2, center };
};

interface RawPoint {
  type: string;
  x: number;
  y: number;
  radius?: number;
  center?: { x: number; y: number };
  ownerIndex?: number;
  speed?: number;
}

interface Point2D {
  x: number;
  y: number;
}

export const getTurnDirection = (p0: Waypoint | RawPoint, p1: Waypoint | RawPoint, p2: Waypoint | RawPoint) => {
  const dx1 = p1.x - p0.x;
  const dy1 = p1.y - p0.y;
  const dx2 = p2.x - p1.x;
  const dy2 = p2.y - p1.y;
  return dx1 * dy2 - dy1 * dx2;
};

export const getMousePositionInSVG = (event: React.MouseEvent, svg: SVGSVGElement) => {
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;

  const ctm = svg.getScreenCTM();
  if (!ctm) return { x: 0, y: 0 };

  const svgP = pt.matrixTransform(ctm.inverse());
  return { x: svgP.x, y: svgP.y };
};

const getClosestPointOnSegment = (point: Point2D, start: Point2D, end: Point2D) => {
  const segmentX = end.x - start.x;
  const segmentY = end.y - start.y;
  const segmentLengthSquared = segmentX * segmentX + segmentY * segmentY;

  if (segmentLengthSquared === 0) {
    return start;
  }

  const projection = ((point.x - start.x) * segmentX + (point.y - start.y) * segmentY) / segmentLengthSquared;
  const clampedProjection = Math.max(0, Math.min(1, projection));

  return {
    x: start.x + clampedProjection * segmentX,
    y: start.y + clampedProjection * segmentY,
  };
};

export const findWaypointWithinRadius = (waypoints: Waypoint[], point: Point2D, radius: number) =>
  waypoints.find((waypoint) => Math.hypot(waypoint.x - point.x, waypoint.y - point.y) < radius) ?? null;

export const findClosestSegmentIndex = (waypoints: Waypoint[], point: Point2D, maxDistance = Infinity) => {
  if (waypoints.length < 2) {
    return null;
  }

  let closestIndex: number | null = null;
  let closestDistance = Infinity;

  for (let index = 0; index < waypoints.length - 1; index++) {
    const closestPoint = getClosestPointOnSegment(point, waypoints[index], waypoints[index + 1]);
    const distanceToSegment = Math.hypot(point.x - closestPoint.x, point.y - closestPoint.y);

    if (distanceToSegment < closestDistance) {
      closestDistance = distanceToSegment;
      closestIndex = index;
    }
  }

  return closestDistance <= maxDistance ? closestIndex : null;
};

/** Prefer curved segment stroke hit-testing in the SVG; fall back to straight-line proximity. */
export const PATH_BUILDER_SEGMENT_HIT_RADIUS_M = 0.3;

export const findClickedSegmentIndex = (
  svg: SVGSVGElement,
  pathId: string,
  svgPoint: Point2D,
  waypoints: Waypoint[],
  maxDistanceM = PATH_BUILDER_SEGMENT_HIT_RADIUS_M,
  fieldHeight?: number
): number | null => {
  const segmentPaths = svg.querySelectorAll<SVGPathElement>('path[data-path-builder-segment]');

  for (const pathEl of segmentPaths) {
    if (pathEl.dataset.pathId !== pathId) continue;
    if (typeof pathEl.isPointInStroke === 'function' && pathEl.isPointInStroke(svgPoint)) {
      const index = pathEl.dataset.segmentIndex;
      if (index !== undefined) {
        return Number(index);
      }
    }
  }

  const fieldPoint = fieldHeight === undefined ? svgPoint : svgPointToField(svgPoint, fieldHeight);
  return findClosestSegmentIndex(waypoints, fieldPoint, maxDistanceM);
};

interface PathBuilderViewportMetricsInput {
  availableWidth: number;
  availableHeight: number;
  fieldWidth: number;
  fieldHeight: number;
  zoom: number;
  overflowLeft?: number;
  overflowRight?: number;
  overflowTop?: number;
  overflowBottom?: number;
}

export interface PathBuilderViewportMetrics {
  fittedFieldWidth: number;
  fittedFieldHeight: number;
  zoomedFieldWidth: number;
  zoomedFieldHeight: number;
  contentWidth: number;
  contentHeight: number;
  fieldOffsetX: number;
  fieldOffsetY: number;
  hasHorizontalOverflow: boolean;
  hasVerticalOverflow: boolean;
}

export interface PathBuilderOverflowMetrics {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

const clampDimension = (value: number) => Math.max(1, value);
const PATH_BUILDER_FIT_PADDING_PX = 2;
const PATH_BUILDER_OUT_OF_BOUNDS_PADDING = 0.12;

export const getPathBuilderOverflowMetrics = ({
  paths,
  fieldWidth,
  fieldHeight,
  fieldRotation = 0,
  axisOverhangM = PATH_BUILDER_AXIS_OVERHANG_M,
}: {
  paths: Path[];
  /** Natural (unrotated) field width in meters. */
  fieldWidth: number;
  /** Natural (unrotated) field height in meters. */
  fieldHeight: number;
  /** View-only field rotation; overflow is measured in viewBox space. */
  fieldRotation?: number;
  /** Extra padding so X/Y axis arrows outside the field remain scrollable. */
  axisOverhangM?: number;
}): PathBuilderOverflowMetrics => {
  const rotation = normalizeFieldViewRotation(fieldRotation);
  const viewWidth = rotation % 180 === 0 ? fieldWidth : fieldHeight;
  const viewHeight = rotation % 180 === 0 ? fieldHeight : fieldWidth;

  const acc: PathBuilderOverflowMetrics = { left: 0, right: 0, top: 0, bottom: 0 };

  const includeViewPoint = (viewPoint: FieldPoint2D, padding: number) => {
    if (viewPoint.x < 0) {
      acc.left = Math.max(acc.left, -viewPoint.x + padding);
    }
    if (viewPoint.x > viewWidth) {
      acc.right = Math.max(acc.right, viewPoint.x - viewWidth + padding);
    }
    if (viewPoint.y < 0) {
      acc.top = Math.max(acc.top, -viewPoint.y + padding);
    }
    if (viewPoint.y > viewHeight) {
      acc.bottom = Math.max(acc.bottom, viewPoint.y - viewHeight + padding);
    }
  };

  paths.forEach((path) => {
    getPathWaypoints(path).forEach((waypoint) => {
      const overflowPadding = Math.max(PATH_BUILDER_OUT_OF_BOUNDS_PADDING, waypoint.rad + PATH_BUILDER_OUT_OF_BOUNDS_PADDING);
      includeViewPoint(fieldPointToView(waypoint, rotation, fieldWidth, fieldHeight), overflowPadding);
    });
  });

  // Axis arrows hang just outside natural origin (field 0,0 → SVG bottom-left).
  if (axisOverhangM > 0) {
    const originSvgY = fieldHeight;
    (
      [
        { x: -axisOverhangM, y: originSvgY },
        { x: 0, y: originSvgY + axisOverhangM },
        { x: -axisOverhangM, y: originSvgY + axisOverhangM },
      ] as FieldPoint2D[]
    ).forEach((naturalPoint) => {
      includeViewPoint(naturalSvgPointToView(naturalPoint, rotation, fieldWidth, fieldHeight), axisOverhangM);
    });
  }

  return acc;
};

export const getPathBuilderViewportMetrics = ({
  availableWidth,
  availableHeight,
  fieldWidth,
  fieldHeight,
  zoom,
  overflowLeft = 0,
  overflowRight = 0,
  overflowTop = 0,
  overflowBottom = 0,
}: PathBuilderViewportMetricsInput): PathBuilderViewportMetrics => {
  const safeAvailableWidth = clampDimension(availableWidth);
  const safeAvailableHeight = clampDimension(availableHeight);
  const safeFieldWidth = Math.max(Number.EPSILON, fieldWidth);
  const safeFieldHeight = Math.max(Number.EPSILON, fieldHeight);
  const safeZoom = Math.max(0.01, zoom);
  const safeOverflowLeft = Math.max(0, overflowLeft);
  const safeOverflowRight = Math.max(0, overflowRight);
  const safeOverflowTop = Math.max(0, overflowTop);
  const safeOverflowBottom = Math.max(0, overflowBottom);
  const fitAvailableWidth = clampDimension(safeAvailableWidth - PATH_BUILDER_FIT_PADDING_PX);
  const fitAvailableHeight = clampDimension(safeAvailableHeight - PATH_BUILDER_FIT_PADDING_PX);

  // Fit field + axis/out-of-bounds overhang so zoom=1 shows the full drawable area without scroll.
  const layoutWidth = safeFieldWidth + safeOverflowLeft + safeOverflowRight;
  const layoutHeight = safeFieldHeight + safeOverflowTop + safeOverflowBottom;
  const fittedScale = Math.min(fitAvailableWidth / layoutWidth, fitAvailableHeight / layoutHeight);
  const fittedFieldWidth = safeFieldWidth * fittedScale;
  const fittedFieldHeight = safeFieldHeight * fittedScale;
  const zoomedFieldWidth = fittedFieldWidth * safeZoom;
  const zoomedFieldHeight = fittedFieldHeight * safeZoom;
  const zoomedScale = fittedScale * safeZoom;
  const extraWidth = (safeOverflowLeft + safeOverflowRight) * zoomedScale;
  const extraHeight = (safeOverflowTop + safeOverflowBottom) * zoomedScale;
  // Round to whole pixels so sub-pixel float noise cannot toggle overflow/scrollbars every frame.
  const totalRenderedWidth = Math.round(zoomedFieldWidth + extraWidth);
  const totalRenderedHeight = Math.round(zoomedFieldHeight + extraHeight);
  const contentWidth = Math.max(safeAvailableWidth, totalRenderedWidth);
  const contentHeight = Math.max(safeAvailableHeight, totalRenderedHeight);
  const centeredOffsetX = (contentWidth - totalRenderedWidth) / 2;
  const centeredOffsetY = (contentHeight - totalRenderedHeight) / 2;

  return {
    fittedFieldWidth,
    fittedFieldHeight,
    zoomedFieldWidth,
    zoomedFieldHeight,
    contentWidth,
    contentHeight,
    fieldOffsetX: centeredOffsetX + safeOverflowLeft * zoomedScale,
    fieldOffsetY: centeredOffsetY + safeOverflowTop * zoomedScale,
    hasHorizontalOverflow: totalRenderedWidth > safeAvailableWidth + 0.5,
    hasVerticalOverflow: totalRenderedHeight > safeAvailableHeight + 0.5,
  };
};

interface PathBuilderViewportScrollPositionInput {
  previousContentWidth: number;
  previousContentHeight: number;
  nextContentWidth: number;
  nextContentHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  scrollLeft: number;
  scrollTop: number;
}

const clampScrollPosition = (value: number, contentSize: number, viewportSize: number) => {
  const maxScroll = Math.max(0, contentSize - viewportSize);
  return Math.max(0, Math.min(maxScroll, value));
};

export const getPathBuilderViewportScrollPosition = ({
  previousContentWidth,
  previousContentHeight,
  nextContentWidth,
  nextContentHeight,
  viewportWidth,
  viewportHeight,
  scrollLeft,
  scrollTop,
}: PathBuilderViewportScrollPositionInput) => {
  const safeViewportWidth = clampDimension(viewportWidth);
  const safeViewportHeight = clampDimension(viewportHeight);
  const safePreviousWidth = clampDimension(previousContentWidth);
  const safePreviousHeight = clampDimension(previousContentHeight);
  const safeNextWidth = clampDimension(nextContentWidth);
  const safeNextHeight = clampDimension(nextContentHeight);

  const horizontalCenterRatio = (scrollLeft + safeViewportWidth / 2) / safePreviousWidth;
  const verticalCenterRatio = (scrollTop + safeViewportHeight / 2) / safePreviousHeight;

  return {
    scrollLeft: clampScrollPosition(horizontalCenterRatio * safeNextWidth - safeViewportWidth / 2, safeNextWidth, safeViewportWidth),
    scrollTop: clampScrollPosition(verticalCenterRatio * safeNextHeight - safeViewportHeight / 2, safeNextHeight, safeViewportHeight),
  };
};
export const generateSegmentPath = (waypoints: Waypoint[], i: number) => {
  const p1 = waypoints[i - 1];
  const p2 = waypoints[i];
  const p3 = waypoints[i + 1];
  const p4 = waypoints[i + 2];

  const p2HasArc = p2?.rad > 0;
  const p3HasArc = p3?.rad > 0;

  if (!p2HasArc && !p3HasArc) {
    return `M ${p2.x},${p2.y} L ${p3.x},${p3.y}`;
  } else if (!p2HasArc && p3HasArc && p4) {
    const sweepFlag = getTurnDirection(p2, p3, p4) < 0 ? 0 : 1;
    const { b, t1 } = computeTangentPoints(p2, p3, p4, p3.rad);
    return `M ${p2.x},${p2.y} L ${t1.x},${t1.y} A ${p3.rad},${p3.rad} 0 0 ${sweepFlag} ${b.x},${b.y}`;
  } else if (p2HasArc && !p3HasArc && p1) {
    const sweepFlag = getTurnDirection(p1, p2, p3) < 0 ? 0 : 1;
    const { b, t2 } = computeTangentPoints(p1, p2, p3, p2.rad);
    return `M ${b.x - ARC_PATH_START_EPSILON_M},${b.y - ARC_PATH_START_EPSILON_M} A ${p2.rad},${p2.rad} 0 0 ${sweepFlag} ${t2.x},${t2.y} L ${p3.x},${
      p3.y
    }`;
  } else if (p2HasArc && p3HasArc && p1 && p4) {
    const sweepFlag1 = getTurnDirection(p1, p2, p3) < 0 ? 0 : 1;
    const sweepFlag2 = getTurnDirection(p2, p3, p4) < 0 ? 0 : 1;
    const { b: b1, t2 } = computeTangentPoints(p1, p2, p3, p2.rad);
    const { b: b2, t1: t3 } = computeTangentPoints(p2, p3, p4, p3.rad);

    return `M ${b1.x - ARC_PATH_START_EPSILON_M},${b1.y - ARC_PATH_START_EPSILON_M} A ${p2.rad},${p2.rad} 0 0 ${sweepFlag1} ${t2.x},${t2.y} L ${
      t3.x
    },${t3.y} A ${p3.rad},${p3.rad} 0 0 ${sweepFlag2} ${b2.x},${b2.y}`;
  }
  return null;
};

export const generateStraightPath = (currentPoint: Waypoint, nextPoint: Waypoint) => {
  return `M ${currentPoint.x},${currentPoint.y} L ${nextPoint.x},${nextPoint.y}`;
};

export const getVectorAngle = (x1: number, y1: number, x2: number, y2: number) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return normalizeRotationDegrees((Math.atan2(dy, dx) * 180) / Math.PI);
};

/**
 * Wraps an angle to the half-open interval [−180, 180): +180 maps to −180,
 * matching robot yaw sign convention.
 */
export const normalizeRotationDegrees = (degrees: number) => {
  return ((((degrees + 180) % 360) + 360) % 360) - 180;
};

/**
 * Persisted/UI integer heading: round first, then wrap into [−180, 180).
 * Rounding before normalize avoids 179.5 → 180 (non-canonical / non-idempotent).
 */
export const canonicalizeRotationDegrees = (degrees: number) => {
  return normalizeRotationDegrees(Math.round(degrees));
};

/**
 * Heading from a field-space direction vector: 0 = +X, +90 = +Y (up).
 */
export const fieldHeadingFromDelta = (dx: number, dy: number) => canonicalizeRotationDegrees((Math.atan2(dy, dx) * 180) / Math.PI);

/**
 * Heading from an SVG-space path tangent (Y down): converts to field yaw.
 */
export const fieldHeadingFromSvgDelta = (dx: number, dy: number) => canonicalizeRotationDegrees((Math.atan2(-dy, dx) * 180) / Math.PI);

export const interpolateWaypointRotation = (start: number, end: number, t: number) => {
  let delta = end - start;
  delta = ((((delta + 180) % 360) + 360) % 360) - 180;
  return normalizeRotationDegrees(start + delta * t);
};

export interface PathMeasureLike {
  getTotalLength: () => number;
  getPointAtLength: (distance: number) => { x: number; y: number };
}

export const getPathTangentRotation = (path: PathMeasureLike, distance: number, sampleWindow: number = 1) => {
  const totalLength = path.getTotalLength();
  if (totalLength <= 0) {
    return 0;
  }

  const clampedDistance = Math.max(0, Math.min(distance, totalLength));
  const startDistance = Math.max(0, clampedDistance - sampleWindow);
  const endDistance = Math.min(totalLength, clampedDistance + sampleWindow);

  if (startDistance === endDistance) {
    return 0;
  }

  const startPoint = path.getPointAtLength(startDistance);
  const endPoint = path.getPointAtLength(endDistance);
  const dx = endPoint.x - startPoint.x;
  const dy = endPoint.y - startPoint.y;

  if (dx === 0 && dy === 0) {
    return 0;
  }

  return fieldHeadingFromSvgDelta(dx, dy);
};

const gradient = tinygradient([
  { color: '#ff0000', pos: 0 },
  { color: '#ff9900', pos: 1 / 3 },
  { color: '#ffff00', pos: 0.5 },
  { color: '#ccff66', pos: 2 / 3 },
  { color: '#00ff00', pos: 1 },
]);

export const speedToColor = (speed: number) => {
  const clamped = Math.min(Math.max(speed, PATH_BUILDER_SPEED_CONSTRAINTS.min), PATH_BUILDER_SPEED_CONSTRAINTS.max);
  const pos = (clamped - PATH_BUILDER_SPEED_CONSTRAINTS.min) / (PATH_BUILDER_SPEED_CONSTRAINTS.max - PATH_BUILDER_SPEED_CONSTRAINTS.min);
  return gradient.rgbAt(pos).toHexString();
};

export interface ColoredSubpath {
  id: string;
  d: string;
  color: string;
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const angleLerp = (start: number, end: number, t: number) => start + (end - start) * t;

/**
 * Blended speed at fraction `t` through a segment, with smooth 15% blend zones
 * at waypoint boundaries between adjacent segment speeds.
 */
const blendedSpeedAlongSegment = (
  t: number,
  segSpeed: number,
  prevSegSpeed: number | null,
  nextSegSpeed: number | null,
  blendFraction: number = 0.15
): number => {
  let speed = segSpeed;
  if (prevSegSpeed != null && t < blendFraction) {
    const blendT = t / blendFraction;
    speed = prevSegSpeed + (segSpeed - prevSegSpeed) * blendT;
  } else if (nextSegSpeed != null && t > 1 - blendFraction) {
    const blendT = (t - (1 - blendFraction)) / blendFraction;
    speed = segSpeed + (nextSegSpeed - segSpeed) * blendT;
  }
  return speed;
};

export const generateColoredSubpaths = (waypoints: Waypoint[], subdivisionsPerSegment: number = 24, spd?: number[]): ColoredSubpath[] => {
  if (waypoints.length < 2) return [];

  const A_LAT = 1;
  const speeds = spd ?? Array.from({ length: waypoints.length - 1 }, () => 1);

  const rawPoints: RawPoint[] = [];
  for (let i = 0; i < waypoints.length; i++) {
    const prev = waypoints[i - 1];
    const curr = waypoints[i];
    const next = waypoints[i + 1];
    if (curr.rad <= 0) {
      rawPoints.push({ type: 'point', x: curr.x, y: curr.y, ownerIndex: i });
    } else if (prev && next) {
      const { t1, t2, center } = computeTangentPoints(prev, curr, next, curr.rad);
      rawPoints.push({ type: 't1', x: t1.x, y: t1.y, ownerIndex: i });
      rawPoints.push({ type: 'arc-point', x: curr.x, y: curr.y, radius: curr.rad, center, ownerIndex: i });
      rawPoints.push({ type: 't2', x: t2.x, y: t2.y, ownerIndex: i });
    } else {
      rawPoints.push({ type: 'point', x: curr.x, y: curr.y, ownerIndex: i });
    }
  }

  type LinePiece = {
    type: 'line';
    fromIndex: number;
    toIndex: number;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    length: number;
    segIndex: number;
  };
  type ArcPiece = {
    type: 'arc';
    fromIndex: number;
    toIndex: number;
    center: { x: number; y: number };
    radius: number;
    startAngle: number;
    delta: number;
    length: number;
    ownerIndex: number;
    segIndex: number;
  };
  type Piece = LinePiece | ArcPiece;

  const pieces: Piece[] = [];

  const rawPointSegIndex = (rp: RawPoint): number => {
    const oi = rp.ownerIndex ?? 0;
    return Math.min(oi, speeds.length - 1);
  };

  for (let i = 0; i < rawPoints.length; i++) {
    const rp = rawPoints[i];
    const prev = rawPoints[i - 1];
    const next = rawPoints[i + 1];
    if (!next) break;

    if (rp.type === 'point' || rp.type === 't2') {
      const len = Math.hypot(next.x - rp.x, next.y - rp.y);
      pieces.push({
        type: 'line',
        fromIndex: i,
        toIndex: i + 1,
        x1: rp.x,
        y1: rp.y,
        x2: next.x,
        y2: next.y,
        length: len,
        segIndex: rawPointSegIndex(rp),
      });
      continue;
    }

    if (rp.type === 'arc-point' && prev && next && rp.center && rp.radius) {
      const center = rp.center;
      const startAngle = Math.atan2(prev.y - center.y, prev.x - center.x);
      const endAngle = Math.atan2(next.y - center.y, next.x - center.x);
      const sweepFlag = getTurnDirection(prev, rp, next) < 0 ? 0 : 1;
      let delta = endAngle - startAngle;
      if (sweepFlag === 1 && delta < 0) delta += Math.PI * 2;
      if (sweepFlag === 0 && delta > 0) delta -= Math.PI * 2;
      const len = Math.abs(delta) * rp.radius;
      const oi = rp.ownerIndex ?? 0;
      const segIdx = oi > 0 ? oi - 1 : 0;
      pieces.push({
        type: 'arc',
        fromIndex: i - 1,
        toIndex: i + 1,
        center,
        radius: rp.radius,
        startAngle,
        delta,
        length: len,
        ownerIndex: rp.ownerIndex ?? 0,
        segIndex: Math.min(segIdx, speeds.length - 1),
      });
      continue;
    }
  }

  const colored: ColoredSubpath[] = [];

  for (let idx = 0; idx < pieces.length; idx++) {
    const piece = pieces[idx];
    const si = piece.segIndex;
    const segSpeed = speeds[si] ?? 1;
    const prevSegSpeed = si > 0 ? speeds[si - 1] : null;
    const nextSegSpeed = si < speeds.length - 1 ? speeds[si + 1] : null;

    if (piece.type === 'line') {
      const segs = subdivisionsPerSegment;
      for (let s = 0; s < segs; s++) {
        const t0 = s / segs;
        const t1 = (s + 1) / segs;
        const lx0 = lerp(piece.x1, piece.x2, t0);
        const ly0 = lerp(piece.y1, piece.y2, t0);
        const lx1 = lerp(piece.x1, piece.x2, t1);
        const ly1 = lerp(piece.y1, piece.y2, t1);
        const tMid = (t0 + t1) / 2;
        const speed = blendedSpeedAlongSegment(tMid, segSpeed, prevSegSpeed, nextSegSpeed);
        colored.push({ id: `line-${idx}-${s}`, d: `M ${lx0},${ly0} L ${lx1},${ly1}`, color: speedToColor(speed) });
      }
      continue;
    }

    if (piece.type === 'arc') {
      const center = piece.center;
      const arcSpeed = Math.min(segSpeed, Math.sqrt(A_LAT * piece.radius));
      const segs = subdivisionsPerSegment;
      for (let s = 0; s < segs; s++) {
        const t0 = s / segs;
        const t1 = (s + 1) / segs;
        const a0 = angleLerp(piece.startAngle, piece.startAngle + piece.delta, t0);
        const a1 = angleLerp(piece.startAngle, piece.startAngle + piece.delta, t1);
        const ax0 = center.x + Math.cos(a0) * piece.radius;
        const ay0 = center.y + Math.sin(a0) * piece.radius;
        const ax1 = center.x + Math.cos(a1) * piece.radius;
        const ay1 = center.y + Math.sin(a1) * piece.radius;
        colored.push({ id: `arc-${idx}-${s}`, d: `M ${ax0},${ay0} L ${ax1},${ay1}`, color: speedToColor(arcSpeed) });
      }
      continue;
    }
  }

  return colored;
};
